import React, { useState, useEffect } from 'react';
import { Play, Pause, Headphones, Brain, Moon, Activity, ArrowRight, CheckCircle2, Waves, Wind, Zap, Sparkles, SlidersHorizontal, Volume2, Target, Music, BookOpen, Shield, Crown, Check, LineChart, Briefcase, Cpu, X, ShieldCheck } from 'lucide-react';
import { Logo } from './components/Logo';
import SEO from './components/SEO';
import SamplePlayerModal from './components/SamplePlayerModal';
import AnimatedWaveCanvas from './components/AnimatedWaveCanvas';
import WavelengthBackground from './components/WavelengthBackground';
import FramerMotionVectorWaves from './components/FramerMotionVectorWaves';

// --- SUB-PAGES ---

function HomeContent({ setPage, onExit, onPlaySample }: { setPage: (page: any) => void, onExit: () => void, onPlaySample: () => void }) {
  return (
    <main id="main-content" tabIndex={-1} className="outline-none">
      <SEO 
        title="Neuros Box | Auditory Tools for Focus & Relaxation"
        description="A guided audio system designed to help improve focus and mental clarity using engineered brainwave frequencies. Explore our tools for sleep, focus, and relaxation."
        url="https://neurosbox.com/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Neuros Box",
            "applicationCategory": "HealthApplication",
            "operatingSystem": "Web, iOS, Android",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "description": "Advanced binaural beats generator and frequency therapy app for sleep, focus, and stress relief."
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Do I need headphones to use Neuros Box?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, headphones are strictly required for binaural beats to work. The illusion of the beat is created in the brain stem when each ear receives a slightly different frequency. Without headphones, the frequencies mix in the air, canceling the effect."
                }
              },
              {
                "@type": "Question",
                "name": "How long does it take for brainwave entrainment to work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most users feel the effects within 5 to 10 minutes of listening. For deep sleep or intense focus sessions, we recommend listening for at least 30 minutes to allow your brain waves to fully synchronize with the audio."
                }
              },
              {
                "@type": "Question",
                "name": "Is frequency therapy safe?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, binaural beats and frequency therapy are completely safe for the vast majority of people. However, if you suffer from epilepsy, are prone to seizures, or wear a pacemaker, you should consult a physician before using brainwave entrainment audio."
                }
              },
              {
                "@type": "Question",
                "name": "What is the difference between white, pink, and brown noise?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "White noise contains all frequencies at equal intensity (like TV static). Pink noise is deeper, with lower frequencies being louder (like steady rain). Brown noise is the deepest, focusing heavily on low frequencies (like a roaring waterfall), making it excellent for sleep and masking tinnitus."
                }
              }
            ]
          }
        ]}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 lg:pt-48 lg:pb-32 overflow-hidden">
        <FramerMotionVectorWaves className="absolute inset-0 w-full h-full opacity-65 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-[800px] h-[90vw] max-h-[800px] bg-emerald-900/20 rounded-full blur-[120px] pointer-events-none mix-blend-screen"></div>
        <div className="absolute top-0 right-0 w-[80vw] max-w-[600px] h-[80vw] max-h-[600px] bg-indigo-900/20 rounded-full blur-[100px] pointer-events-none mix-blend-screen"></div>

        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-1000">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-emerald-400 mb-8 uppercase tracking-widest">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Advanced Binaural Beats Generator
            </div>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter text-white mb-8 leading-[0.9]">
              The 16-Session Brainwave System <br className="hidden md:block" />
              <span className="font-serif italic text-emerald-400">for mental clarity.</span>
            </h1>
            <p className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 font-light leading-relaxed">
              Stop relying on random audio files. Follow a structured protocol designed to help improve focus and mental clarity using engineered <strong className="text-zinc-200 font-medium">brainwave frequencies</strong>. A clinical-grade daily system for deep work, restful sleep, and relaxation.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="#pricing" onClick={(e) => { e.preventDefault(); setPage('pricing'); }} className="w-full sm:w-auto px-8 py-4 bg-emerald-500 text-black rounded-full font-medium text-lg hover:bg-emerald-400 transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black">
                Start the 16-Session Protocol <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </a>
              <button onClick={onPlaySample} className="w-full sm:w-auto px-8 py-4 bg-white/5 text-white border border-white/10 rounded-full font-medium text-lg hover:bg-white/10 transition-all flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black">
                <Play className="w-5 h-5" aria-hidden="true" /> Listen to Sample
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Product Showcase (App UI Mockup) */}
      <section className="py-20 px-6 bg-zinc-950 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-light tracking-tight text-white mb-4">A structured cognitive system, <br className="hidden md:block"/> beautifully designed.</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">Follow guided multi-day programs or blend up to 8 distinct audio layers—including progressive binaural beats and color noises—in a fully immersive, 3D-spatialized interface.</p>
          </div>

          <div className="relative mx-auto max-w-5xl rounded-xl border border-white/10 bg-zinc-900 shadow-2xl overflow-hidden mt-12 transform hover:scale-[1.01] transition-transform duration-700">
            {/* Mac OS Header */}
            <div className="flex items-center px-4 py-3 border-b border-white/5 bg-black/50">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-rose-500/80"></div>
                <div className="w-3 h-3 rounded-full bg-amber-500/80"></div>
                <div className="w-3 h-3 rounded-full bg-emerald-500/80"></div>
              </div>
              <div className="mx-auto text-xs font-medium text-zinc-500 font-mono tracking-wider uppercase">Neuros Pro Mixer</div>
            </div>
            {/* Body */}
            <div className="grid md:grid-cols-4 h-auto md:h-[500px]">
              {/* Sidebar */}
              <div className="border-r border-white/5 p-6 space-y-8 hidden md:flex flex-col bg-black/20">
                <div className="space-y-4">
                  <div className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-4">Library</div>
                  <div className="h-10 w-full bg-white/5 rounded-lg flex items-center px-4 border border-emerald-500/20 text-emerald-400 text-sm font-medium gap-3"><Sparkles className="w-4 h-4" /> Smart Mode AI</div>
                  <div className="h-10 w-full hover:bg-white/5 rounded-lg flex items-center px-4 text-zinc-400 text-sm gap-3 transition-colors"><Brain className="w-4 h-4" /> Deep Focus</div>
                  <div className="h-10 w-full hover:bg-white/5 rounded-lg flex items-center px-4 text-zinc-400 text-sm gap-3 transition-colors"><Moon className="w-4 h-4" /> Sleep Prep</div>
                  <div className="h-10 w-full hover:bg-white/5 rounded-lg flex items-center px-4 text-zinc-400 text-sm gap-3 transition-colors"><Activity className="w-4 h-4" /> Stress Relief</div>
                </div>
                <div className="mt-auto space-y-4 pt-6 border-t border-white/5">
                  <div className="p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl relative overflow-hidden">
                     <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/10 to-transparent"></div>
                     <p className="text-indigo-400 text-xs font-medium mb-1 relative z-10">Daily Goal Active</p>
                     <p className="text-white text-sm relative z-10">45m Deep Work</p>
                  </div>
                </div>
              </div>
              {/* Main Content (Mixers) */}
              <div className="col-span-3 p-6 md:p-10 relative overflow-hidden bg-black/40">
                {/* Background glow */}
                <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none"></div>
                
                <div className="flex items-center justify-between mb-8 relative z-10">
                  <div>
                    <h3 className="text-2xl text-white font-light tracking-tight mb-1">Hyper Focus State</h3>
                    <p className="text-zinc-400 text-sm">Gamma Entrainment • 40Hz</p>
                  </div>
                  <div className="flex items-center gap-4">
                     <button aria-label="Toggle sample audio frequency preview" title="Toggle sample audio frequency preview" className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center bg-black hover:bg-white/5 transition-colors">
                       <Volume2 className="w-5 h-5 text-white" aria-hidden="true" />
                       <span className="sr-only">Toggle sample audio frequency preview</span>
                     </button>
                     <button className="px-6 py-3 rounded-full bg-emerald-500 text-black font-medium flex items-center gap-2 hover:bg-emerald-400 transition-colors"><Pause className="w-4 h-4" aria-hidden="true" /> Playing</button>
                  </div>
                </div>

                <div className="space-y-4 relative z-10">
                  {/* Sliders */}
                  <div className="bg-zinc-900/80 border border-white/5 p-5 rounded-2xl flex items-center gap-6 backdrop-blur-md">
                     <div className="w-12 h-12 rounded-xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center shrink-0"><Brain className="w-6 h-6 text-indigo-400"/></div>
                     <div className="flex-1">
                       <div className="flex justify-between text-sm mb-3">
                         <span className="text-white font-medium">Binaural Beats (Gamma 40Hz)</span>
                         <span className="text-emerald-400 font-medium">75%</span>
                       </div>
                       <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 w-[75%] rounded-full shadow-[0_0_10px_rgba(16,185,129,0.5)]"></div></div>
                     </div>
                  </div>
                  <div className="bg-zinc-900/80 border border-white/5 p-5 rounded-2xl flex items-center gap-6 backdrop-blur-md">
                     <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center shrink-0"><Wind className="w-6 h-6 text-amber-400"/></div>
                     <div className="flex-1">
                       <div className="flex justify-between text-sm mb-3">
                         <span className="text-white font-medium">Pink Noise (Acoustic Masking)</span>
                         <span className="text-emerald-400 font-medium">40%</span>
                       </div>
                       <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 w-[40%] rounded-full"></div></div>
                     </div>
                  </div>
                  <div className="bg-zinc-900/80 border border-white/5 p-5 rounded-2xl flex items-center gap-6 backdrop-blur-md">
                     <div className="w-12 h-12 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0"><Waves className="w-6 h-6 text-emerald-400"/></div>
                     <div className="flex-1">
                       <div className="flex justify-between text-sm mb-3">
                         <span className="text-white font-medium">Deep Carrier Tones (120Hz)</span>
                         <span className="text-emerald-400 font-medium">60%</span>
                       </div>
                       <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 w-[60%] rounded-full"></div></div>
                     </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dashboard Showcase (Analytics/Sessions Mockup) */}
          <div className="relative mx-auto max-w-5xl rounded-xl border border-white/10 bg-zinc-900 shadow-2xl overflow-hidden mt-16 transform hover:scale-[1.01] transition-transform duration-700">
            {/* Mac OS Header */}
            <div className="flex items-center px-4 py-3 border-b border-white/5 bg-black/50">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-rose-500/80"></div>
                <div className="w-3 h-3 rounded-full bg-amber-500/80"></div>
                <div className="w-3 h-3 rounded-full bg-emerald-500/80"></div>
              </div>
              <div className="mx-auto text-xs font-medium text-zinc-500 font-mono tracking-wider uppercase">Neuros Performance Dashboard</div>
            </div>
            
            <div className="p-6 md:p-10 bg-black/40 relative overflow-hidden">
               {/* Background glow */}
               <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-[100px] pointer-events-none"></div>

              {/* Top Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 relative z-10">
                <div className="bg-zinc-900/70 border border-white/5 p-6 rounded-2xl backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-zinc-500 text-sm font-medium">Total Listening Time</h4>
                    <LineChart className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="text-4xl text-white font-light">128<span className="text-lg text-zinc-500 ml-1">hrs</span></div>
                  <div className="text-emerald-400 text-xs font-medium mt-3 flex items-center gap-1 bg-emerald-500/10 w-fit px-2 py-1 rounded-md">+12% this week</div>
                </div>
                <div className="bg-zinc-900/70 border border-white/5 p-6 rounded-2xl backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-zinc-500 text-sm font-medium">Current Streak</h4>
                    <Zap className="w-4 h-4 text-amber-400" />
                  </div>
                  <div className="text-4xl text-white font-light">14<span className="text-lg text-zinc-500 ml-1">days</span></div>
                  <div className="text-amber-400 text-xs font-medium mt-3 flex items-center gap-1 bg-amber-500/10 w-fit px-2 py-1 rounded-md">Optimal Consistency</div>
                </div>
                <div className="bg-zinc-900/70 border border-white/5 p-6 rounded-2xl backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-zinc-500 text-sm font-medium">Dominant State</h4>
                    <Activity className="w-4 h-4 text-indigo-400" />
                  </div>
                  <div className="text-4xl text-white font-light">Focus</div>
                  <div className="text-indigo-400 text-xs font-medium mt-3 flex items-center gap-1 bg-indigo-500/10 w-fit px-2 py-1 rounded-md">Gamma 40Hz Bias</div>
                </div>
              </div>

              {/* Session Breakdown */}
              <div className="relative z-10">
                <h3 className="text-xl text-white font-medium mb-6">Recent Cognitive Sessions</h3>
                <div className="space-y-4">
                  {/* Session 1 */}
                  <div className="bg-zinc-900/80 border border-white/5 p-6 rounded-2xl flex flex-col md:flex-row md:items-center gap-4 justify-between hover:bg-white/5 transition-colors cursor-default">
                    <div className="flex items-center gap-5">
                      <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
                        <Brain className="w-7 h-7 text-emerald-400" />
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-white">Session 1: Deep Work & Focus</h4>
                        <p className="text-sm text-zinc-400 mt-1">Gamma 40Hz • 85% Estimated Neural Efficiency</p>
                      </div>
                    </div>
                    <div className="md:text-right flex items-center md:block justify-between mt-2 md:mt-0 md:border-l md:border-white/5 md:pl-6">
                      <div className="text-2xl text-white font-light">45<span className="text-sm text-zinc-500 ml-1">min</span></div>
                      <div className="text-xs text-zinc-500 mt-1">2 hours ago</div>
                    </div>
                  </div>

                  {/* Session 2 */}
                  <div className="bg-zinc-900/80 border border-white/5 p-6 rounded-2xl flex flex-col md:flex-row md:items-center gap-4 justify-between hover:bg-white/5 transition-colors cursor-default">
                    <div className="flex items-center gap-5">
                      <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center shrink-0">
                        <Target className="w-7 h-7 text-indigo-400" />
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-white">Session 2: Memory Consolidation</h4>
                        <p className="text-sm text-zinc-400 mt-1">Theta 7Hz • 92% Projected Retention State</p>
                      </div>
                    </div>
                    <div className="md:text-right flex items-center md:block justify-between mt-2 md:mt-0 md:border-l md:border-white/5 md:pl-6">
                      <div className="text-2xl text-white font-light">30<span className="text-sm text-zinc-500 ml-1">min</span></div>
                      <div className="text-xs text-zinc-500 mt-1">Yesterday</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto mt-16">
            {/* AI Generator Mockup */}
            <div className="relative rounded-xl border border-white/10 bg-zinc-900 shadow-2xl overflow-hidden transform hover:-translate-y-1 transition-transform duration-700">
              <div className="flex items-center px-4 py-3 border-b border-white/5 bg-black/50">
                <div className="flex gap-2">
                   <div className="w-3 h-3 rounded-full bg-zinc-700"></div>
                   <div className="w-3 h-3 rounded-full bg-zinc-700"></div>
                </div>
                <div className="mx-auto text-xs font-medium text-zinc-500 font-mono tracking-wider uppercase">Context-Aware AI</div>
              </div>
              
              <div className="p-8 h-full bg-black/40 relative overflow-hidden flex flex-col">
                 <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-[80px] pointer-events-none"></div>
                 
                 <h3 className="text-xl text-white font-medium mb-6 flex items-center gap-2 relative z-10">
                   <Sparkles className="w-5 h-5 text-indigo-400" /> Goal Synthesizer
                 </h3>
                 <div className="space-y-6 relative z-10 flex-1">
                   <div className="space-y-2">
                     <label className="text-xs text-zinc-500 uppercase tracking-widest font-bold">Current Status</label>
                     <div className="p-3 rounded-lg bg-zinc-900 border border-white/5 text-zinc-300 text-sm">Afternoon brain fog & fatigue.</div>
                   </div>
                   <div className="space-y-2">
                     <label className="text-xs text-zinc-500 uppercase tracking-widest font-bold">Desired Outcome</label>
                     <div className="p-3 rounded-lg bg-zinc-900 border border-indigo-500/50 text-white text-sm flex items-center justify-between shadow-[0_0_15px_rgba(99,102,241,0.1)]">
                       90 minutes of uninterrupted coding.
                       <div className="w-1.5 h-4 bg-indigo-500 animate-pulse"></div>
                     </div>
                   </div>

                   <div className="pt-4 mt-auto border-t border-white/5 space-y-3">
                     <div className="flex items-center gap-3 text-emerald-400 text-sm font-medium">
                       <Check className="w-4 h-4 shrink-0" />
                       Biometrics synced (14:30 Local Time)
                     </div>
                     <div className="p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20 backdrop-blur-md">
                       <h4 className="text-indigo-300 text-xs font-bold uppercase tracking-widest mb-1">Generated Mix</h4>
                       <p className="text-white text-sm font-medium">Gamma Burst (38Hz) + Brown Noise Carrier</p>
                     </div>
                   </div>
                 </div>
              </div>
            </div>

            {/* Mobile / Spatial Audio Mix */}
            <div className="relative rounded-xl border border-white/10 bg-zinc-900 shadow-2xl overflow-hidden transform hover:-translate-y-1 transition-transform duration-700">
              <div className="flex items-center px-4 py-3 border-b border-white/5 bg-black/50">
                <div className="flex gap-2">
                   <div className="w-3 h-3 rounded-full bg-zinc-700"></div>
                   <div className="w-3 h-3 rounded-full bg-zinc-700"></div>
                </div>
                <div className="mx-auto text-xs font-medium text-zinc-500 font-mono tracking-wider uppercase">3D Spatial Physics</div>
              </div>
              
              <div className="p-8 h-full bg-black/40 relative overflow-hidden flex flex-col items-center justify-center">
                 <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-amber-500/10 rounded-full blur-[80px] pointer-events-none"></div>
                 
                 <div className="relative z-10 w-full max-w-[280px] aspect-square rounded-full border border-white/10 flex items-center justify-center">
                    {/* Orbit rings */}
                    <div className="absolute inset-4 rounded-full border border-white/5"></div>
                    <div className="absolute inset-12 rounded-full border border-dashed border-white/10 animate-[spin_20s_linear_infinite]">
                       <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 bg-amber-500/20 rounded-full border border-amber-500/50 flex items-center justify-center">
                         <div className="w-2 h-2 bg-amber-400 rounded-full shadow-[0_0_10px_rgba(251,191,36,1)]"></div>
                       </div>
                    </div>
                    
                    <div className="absolute inset-20 rounded-full border border-white/5 animate-[spin_12s_linear_infinite_reverse]">
                       <div className="absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-6 bg-emerald-500/20 rounded-full border border-emerald-500/50 flex items-center justify-center">
                         <div className="w-2 h-2 bg-emerald-400 rounded-full shadow-[0_0_10px_rgba(16,185,129,1)]"></div>
                       </div>
                    </div>

                    {/* Listener (Center) */}
                    <div className="w-16 h-16 bg-zinc-800 rounded-full border border-white/10 shadow-[0_0_30px_rgba(0,0,0,0.5)] flex items-center justify-center relative z-20">
                      <Headphones className="w-6 h-6 text-white" />
                    </div>
                 </div>

                 <div className="text-center mt-8 relative z-10">
                   <h3 className="text-xl text-white font-medium mb-2">Orbital Acoustics</h3>
                   <p className="text-zinc-400 text-sm">True 3D Doppler panning wraps frequencies around your head, drastically increasing entrainment speed.</p>
                 </div>
              </div>
            </div>
          </div>
        </div>

        {/* Audio Player Preview component */}
        <div className="max-w-5xl mx-auto mt-24 p-8 md:p-12 bg-zinc-900/30 border border-white/5 rounded-3xl relative overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/5 to-indigo-500/5 mix-blend-screen"></div>
           <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center">
             <div>
               <h2 className="text-3xl font-light tracking-tight text-white mb-4">Hear the difference</h2>
               <p className="text-zinc-400 mb-8 leading-relaxed">Experience our clinical-grade audio engine directly in your browser. Connect your headphones, select a target state, and listen to a live sample of Neuros Box's frequency therapy.</p>
               <button onClick={onPlaySample} className="px-8 py-4 bg-emerald-500 hover:bg-emerald-400 text-black font-medium rounded-full flex items-center gap-3 transition-transform hover:scale-105 active:scale-95 shadow-[0_0_30px_rgba(16,185,129,0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black">
                 <Headphones className="w-5 h-5" /> Launch Audio Preview
               </button>
             </div>
             <div className="grid grid-cols-2 gap-4">
                <div className="bg-black/40 border border-white/5 p-6 rounded-2xl flex flex-col items-center justify-center text-center aspect-square hover:bg-white/5 transition-colors cursor-pointer group" onClick={onPlaySample}>
                   <Moon className="w-8 h-8 text-indigo-400 mb-4 group-hover:scale-110 transition-transform" />
                   <h3 className="text-sm font-medium text-white mb-1">Deep Sleep</h3>
                   <p className="text-xs text-zinc-500">Delta • 2Hz</p>
                </div>
                <div className="bg-black/40 border border-white/5 p-6 rounded-2xl flex flex-col items-center justify-center text-center aspect-square hover:bg-white/5 transition-colors cursor-pointer group" onClick={onPlaySample}>
                   <Brain className="w-8 h-8 text-emerald-400 mb-4 group-hover:scale-110 transition-transform" />
                   <h3 className="text-sm font-medium text-white mb-1">Intense Focus</h3>
                   <p className="text-xs text-zinc-500">Gamma • 40Hz</p>
                </div>
                <div className="bg-black/40 border border-white/5 p-6 rounded-2xl flex flex-col items-center justify-center text-center col-span-2 hover:bg-white/5 transition-colors flex-row gap-6 cursor-pointer group" onClick={onPlaySample}>
                   <Activity className="w-8 h-8 text-amber-400 mb-0 group-hover:scale-110 transition-transform" />
                   <div className="text-left">
                     <h3 className="text-sm font-medium text-white mb-1">Rapid Stress Relief</h3>
                     <p className="text-xs text-zinc-500">Alpha • 10Hz • Deep Pink Noise</p>
                   </div>
                </div>
             </div>
           </div>
        </div>
      </section>

      {/* What Science Says / What This Is NOT */}
      <section className="py-24 px-6 bg-[#050505] border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-bold text-emerald-400 uppercase tracking-widest mx-auto">
              <ShieldCheck className="w-3 h-3" /> Expert Reviewed & Fact Checked
            </div>
            <h2 className="text-3xl md:text-5xl font-light tracking-tight text-white mb-4">Evidence-based acoustic engineering</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">We don't rely on hype. Our platform is built on decades of peer-reviewed research regarding acoustic stimulation, brainwave entrainment, and auditory masking.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* What Science Says */}
            <div className="space-y-8">
              <div className="flex items-center gap-3 mb-6">
                <BookOpen className="w-6 h-6 text-emerald-400" />
                <h3 className="text-2xl font-medium text-white">What the science says</h3>
              </div>
              
              <div className="space-y-6">
                <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/5 hover:border-emerald-500/30 transition-colors">
                  <h4 className="text-white font-medium mb-3">Auditory Beat Stimulation limits anxiety</h4>
                  <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                    A meta-analysis of clinical trials demonstrates that listening to binaural beats significantly reduces trait anxiety and improves measures of autonomic arousal.
                  </p>
                  <a href="https://pubmed.ncbi.nlm.nih.gov/31439746/" target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-400 font-medium hover:text-emerald-300 flex items-center gap-1">
                    Read the meta-analysis on PubMed <ArrowRight className="w-3 h-3" />
                  </a>
                </div>

                <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/5 hover:border-emerald-500/30 transition-colors">
                  <h4 className="text-white font-medium mb-3">Gamma entrainment enhances focus and memory</h4>
                  <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                    Research indicates that 40Hz (Gamma) sensory stimulation induces neural synchronization, which is associated with heightened focus, cognitive processing, and memory recall.
                  </p>
                  <a href="https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6433299/" target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-400 font-medium hover:text-emerald-300 flex items-center gap-1">
                    Read the study on PMC <ArrowRight className="w-3 h-3" />
                  </a>
                </div>

                <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/5 hover:border-emerald-500/30 transition-colors">
                  <h4 className="text-white font-medium mb-3">Pink noise improves deep sleep stability</h4>
                  <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                    Acoustic enhancement of slow oscillations during sleep (using pink noise) has been shown to deepen sleep and significantly boost declarative memory in healthy adults.
                  </p>
                  <a href="https://www.frontiersin.org/articles/10.3389/fnhum.2017.00109/full" target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-400 font-medium hover:text-emerald-300 flex items-center gap-1">
                    Read the findings on Frontiers <ArrowRight className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>

            {/* What this is NOT */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Shield className="w-6 h-6 text-rose-400" />
                <h3 className="text-2xl font-medium text-white">What this is <span className="text-rose-400 font-bold">NOT</span></h3>
              </div>
              
              <div className="p-8 rounded-3xl bg-rose-950/10 border border-rose-900/30 relative overflow-hidden h-full">
                <div className="absolute top-0 right-0 p-32 bg-rose-500/5 blur-3xl rounded-full"></div>
                <p className="text-zinc-300 mb-8 relative z-10">
                  We believe in total transparency. While acoustic therapy is powerful, it is important to set realistic expectations. Neuros Box is a tool for cognitive optimization, not magic.
                </p>

                <ul className="space-y-6 relative z-10">
                  <li className="flex items-start gap-4">
                    <X className="w-6 h-6 text-rose-500 shrink-0" />
                    <div>
                      <strong className="text-white block mb-1">Not a medical treatment.</strong>
                      <span className="text-zinc-400 text-sm leading-relaxed">This app does not cure insomnia, ADHD, clinical depression, or clinical anxiety. It is a complementary tool designed to support a healthy lifestyle.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <X className="w-6 h-6 text-rose-500 shrink-0" />
                    <div>
                      <strong className="text-white block mb-1">Not effective without headphones.</strong>
                      <span className="text-zinc-400 text-sm leading-relaxed">Binaural beats require stereo separation. If you play them through laptop speakers or a single phone speaker, the brain wave entrainment effect will not work.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <X className="w-6 h-6 text-rose-500 shrink-0" />
                    <div>
                      <strong className="text-white block mb-1">Not an instant "hack".</strong>
                      <span className="text-zinc-400 text-sm leading-relaxed">While many feel immediate relaxation, profound neuroplastic changes require consistent, disciplined use over days and weeks.</span>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <X className="w-6 h-6 text-rose-500 shrink-0" />
                    <div>
                      <strong className="text-white block mb-1">Not mystical pseudoscience.</strong>
                      <span className="text-zinc-400 text-sm leading-relaxed">We do not claim our frequencies align chakras or manifest wealth. We rely strictly on the bio-mechanics of auditory masking and cortico-thalamic entrainment.</span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Comparison Section (Why Us vs Them) */}
      <section className="py-24 px-6 bg-zinc-950 border-t border-white/5 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-emerald-900/5 mix-blend-screen pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-1/2 h-full bg-rose-900/5 mix-blend-screen pointer-events-none"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-light tracking-tight text-white mb-4">Why not just use YouTube or Spotify?</h2>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
              Mainstream streaming platforms destroy the delicate phase relationships required for binaural beats to actually work.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-0 max-w-4xl mx-auto">
            {/* The "Them" Column */}
            <div className="p-8 md:p-12 bg-zinc-900 border border-white/5 md:rounded-l-3xl">
               <h3 className="text-xl font-medium text-rose-400 mb-6 flex items-center gap-2">
                 <X className="w-5 h-5" /> YouTube / Streaming / Brain.fm
               </h3>
               <ul className="space-y-6">
                 <li className="flex flex-col gap-1">
                   <span className="text-white font-medium text-sm">MP3 Compression</span>
                   <span className="text-zinc-500 text-xs leading-relaxed">Aggressive compression algorithms destroy the micro-timing required for your brain to process the FFR (Frequency Following Response).</span>
                 </li>
                 <li className="flex flex-col gap-1">
                   <span className="text-white font-medium text-sm">Unstructured "Magic Pill" Audio</span>
                   <span className="text-zinc-500 text-xs leading-relaxed">Random 10-hour loops with no clear progression. Just hit play and hope it works, with zero structure or tracking.</span>
                 </li>
                 <li className="flex flex-col gap-1">
                   <span className="text-white font-medium text-sm">Abrupt Ads</span>
                   <span className="text-zinc-500 text-xs leading-relaxed">Nothing kills a deep meditation or intense flow state faster than a sudden, blasting mid-roll deodorant ad. Neuros Box is 100% ad-free.</span>
                 </li>
               </ul>
            </div>
            
            {/* The "Us" Column */}
            <div className="p-8 md:p-12 bg-emerald-950/20 border-2 border-emerald-500/20 md:rounded-r-3xl relative transform md:scale-105 shadow-2xl">
               <div className="absolute -top-3 right-8 bg-emerald-500 text-black text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest">
                 The Neuros Difference
               </div>
               <h3 className="text-xl font-medium text-emerald-400 mb-6 flex items-center gap-2">
                 <Check className="w-5 h-5" /> Neuros Box
               </h3>
               <ul className="space-y-6">
                 <li className="flex flex-col gap-1">
                   <span className="text-white font-medium text-sm">Web Audio API Synthesis</span>
                   <span className="text-emerald-500/80 text-xs leading-relaxed">Frequencies are mathematically generated live in your browser using pure, uncompressed oscillators. Zero data loss.</span>
                 </li>
                 <li className="flex flex-col gap-1">
                   <span className="text-white font-medium text-sm">Structured Cognitive Protocols</span>
                   <span className="text-emerald-500/80 text-xs leading-relaxed">Follow day-by-day programs (like the 14-Day Focus Reset) or build your own environment with the 8-Layer Ambient Mixer.</span>
                 </li>
                 <li className="flex flex-col gap-1">
                   <span className="text-white font-medium text-sm">Complete Offline Capability</span>
                   <span className="text-emerald-500/80 text-xs leading-relaxed">Install the app directly to your phone. Zero ads, zero tracking, works flawlessly on airplanes or in deep nature—no subscriptions required for basic use.</span>
                 </li>
               </ul>
               
               <a href="#pricing" onClick={(e) => { e.preventDefault(); setPage('pricing'); }} className="mt-8 w-full py-4 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 rounded-full font-medium text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 inline-flex items-center justify-center gap-2">
                 Unlock The Complete System <ArrowRight className="w-4 h-4" />
               </a>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof / Risk Reversal / Pricing CTA */}
      <section className="py-32 px-6 relative overflow-hidden bg-zinc-950 border-t border-white/5">
        <div className="absolute inset-0 bg-emerald-900/10 mix-blend-screen pointer-events-none"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-light tracking-tight text-white mb-8">
            Real outcomes from <span className="font-medium text-emerald-400">consistent use.</span>
          </h2>
          <p className="text-xl text-zinc-400 mb-16 max-w-3xl mx-auto">
            Used daily for 14 days ➔ <strong className="text-white">reported improved focus during deep work sessions.</strong>
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 my-16 text-left">
            <div className="p-8 rounded-3xl bg-black/40 border border-white/5 relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-6">
                 <Target className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg text-white font-medium mb-4">Focus Endurance</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                <strong className="text-zinc-200">Before:</strong> Couldn't focus more than 20 minutes without reaching for a phone.<br/><br/>
                <strong className="text-emerald-400">After 14 Days:</strong> Sustaining 60–90 minute uninterrupted deep work blocks.
              </p>
            </div>
            
            <div className="p-8 rounded-3xl bg-black/40 border border-white/5 relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-6">
                 <Moon className="w-6 h-6 text-indigo-400" />
              </div>
              <h3 className="text-lg text-white font-medium mb-4">Sleep Latency</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                <strong className="text-zinc-200">Before:</strong> Racing thoughts causing 45+ minutes tossing and turning in bed.<br/><br/>
                <strong className="text-indigo-400">After 14 Days:</strong> 12-minute average time-to-sleep using Delta entrainment.
              </p>
            </div>

            <div className="p-8 rounded-3xl bg-black/40 border border-white/5 relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-6">
                 <Wind className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg text-white font-medium mb-4">Stress Recovery</h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                <strong className="text-zinc-200">Before:</strong> Afternoon crashes and mid-day overwhelm leading to burnout.<br/><br/>
                <strong className="text-amber-400">After 14 Days:</strong> Using 15-minute Alpha resets to clear cognitive fatigue.
              </p>
            </div>
          </div>

          <a href="#pricing" onClick={(e) => { e.preventDefault(); setPage('pricing'); }} className="px-10 py-5 bg-emerald-500 text-black rounded-full font-medium text-xl hover:bg-emerald-400 transition-all hover:scale-105 active:scale-95 shadow-[0_0_40px_rgba(16,185,129,0.3)] flex items-center justify-center gap-3 mx-auto w-fit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black">
            <Headphones className="w-6 h-6" aria-hidden="true" /> Unlock The Complete System
          </a>
          <p className="mt-6 text-zinc-500 text-sm">Cancel anytime directly from your dashboard.</p>
        </div>
      </section>
    </main>
  );
}

function FeaturesContent({ setPage, onExit }: { setPage: (page: any) => void, onExit: () => void }) {
  return (
    <div className="animate-in fade-in duration-700">
      <SEO 
        title="Tools & Features | Neuros Box"
        description="Explore our clinical-grade audio tools, including the 8-layer ambient mixer, customizable binaural beats, and offline spatial audio controls."
        url="https://neurosbox.com/features"
      />
      <section className="pt-32 pb-20 px-6 lg:pt-48 lg:pb-32 relative">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-light tracking-tighter text-white mb-8">
            A comprehensive <span className="font-serif italic text-indigo-400">frequency protocol</span>
          </h1>
          <p className="text-xl text-zinc-400 leading-relaxed mb-12">
            Explore our clinical-grade <strong>frequency therapy</strong> tools. Our platform combines evidence-based <strong>sound healing frequencies</strong> with modern neuroscience to create a customizable <strong>sleep sound mixer</strong> and <strong>noise generator</strong>.
          </p>
        </div>

        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 mt-16">
          <div className="bg-zinc-900/50 border border-white/5 rounded-3xl p-10">
            <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-8">
              <Activity className="w-8 h-8 text-indigo-400" aria-hidden="true" />
            </div>
            <h2 className="text-3xl font-medium text-white mb-4">Brainwave Frequencies</h2>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Target specific states of consciousness using our precision-engineered <strong>brainwave frequencies</strong>. Whether you need <strong>binaural beats for anxiety</strong> or <strong>binaural beats for focus</strong>, we cover the entire spectrum:
            </p>
            <ul className="space-y-4">
              <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" aria-hidden="true" /><span className="text-zinc-300"><strong>Alpha brain waves (8-14 Hz):</strong> Ideal for relaxation, light meditation, and reducing stress.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" aria-hidden="true" /><span className="text-zinc-300"><strong>Theta brain waves (4-8 Hz):</strong> Perfect for deep <strong>brainwave meditation</strong>, creativity, and REM sleep.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" aria-hidden="true" /><span className="text-zinc-300"><strong>Delta brain waves (0.5-4 Hz):</strong> The foundation of <strong>delta waves sleep</strong>, essential for restorative, deep sleep and healing.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" aria-hidden="true" /><span className="text-zinc-300"><strong>Gamma brain waves (30-100 Hz):</strong> For peak cognitive performance, intense focus, and heightened perception.</span></li>
            </ul>
          </div>

          <div className="bg-zinc-900/50 border border-white/5 rounded-3xl p-10">
            <div className="w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-8">
              <Sparkles className="w-8 h-8 text-amber-400" aria-hidden="true" />
            </div>
            <h2 className="text-3xl font-medium text-white mb-4">Solfeggio Frequencies</h2>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Unlock the power of ancient <strong>solfeggio frequencies</strong>. These specific tones are revered in <strong>healing frequencies</strong> for their unique properties and ability to harmonize the mind and body:
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-black/50 rounded-xl border border-white/5"><span className="text-amber-400 font-medium block">396 Hz frequency</span><span className="text-xs text-zinc-500">Liberating Guilt & Fear</span></div>
              <div className="p-4 bg-black/50 rounded-xl border border-white/5"><span className="text-amber-400 font-medium block">417 Hz frequency</span><span className="text-xs text-zinc-500">Undoing Situations & Change</span></div>
              <div className="p-4 bg-black/50 rounded-xl border border-white/5"><span className="text-amber-400 font-medium block">528 Hz frequency</span><span className="text-xs text-zinc-500">DNA Repair & Miracles</span></div>
              <div className="p-4 bg-black/50 rounded-xl border border-white/5"><span className="text-amber-400 font-medium block">639 Hz frequency</span><span className="text-xs text-zinc-500">Connecting & Relationships</span></div>
              <div className="p-4 bg-black/50 rounded-xl border border-white/5"><span className="text-amber-400 font-medium block">741 Hz frequency</span><span className="text-xs text-zinc-500">Intuition & Awakening</span></div>
              <div className="p-4 bg-black/50 rounded-xl border border-white/5"><span className="text-amber-400 font-medium block">852 Hz frequency</span><span className="text-xs text-zinc-500">Spiritual Order & Awareness</span></div>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto mt-12 bg-zinc-900/50 border border-white/5 rounded-3xl p-10">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-medium text-white mb-4">Advanced Noise Generator</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Our sophisticated <strong>noise generator</strong> allows you to blend various color noises to mask disruptive sounds and create a serene environment.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                <Wind className="w-6 h-6 text-white" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">White Noise</h3>
              <p className="text-sm text-zinc-400">Consistent across all frequencies. Excellent <strong>white noise for sleep</strong> and blocking out sudden background noises.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-pink-500/10 flex items-center justify-center mx-auto mb-4">
                <Wind className="w-6 h-6 text-pink-400" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Pink Noise</h3>
              <p className="text-sm text-zinc-400">Deeper and softer than white noise. <strong>Pink noise sleep</strong> is proven to enhance deep sleep and memory consolidation.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-orange-900/20 flex items-center justify-center mx-auto mb-4">
                <Wind className="w-6 h-6 text-orange-700" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Brown Noise</h3>
              <p className="text-sm text-zinc-400">The deepest of the color noises. <strong>Brown noise sleep</strong> provides a soothing rumble, perfect for relaxation and focus.</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-12 grid md:grid-cols-3 gap-8">
          <div className="bg-zinc-900/50 border border-white/5 rounded-3xl p-10">
            <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-8">
              <Cpu className="w-8 h-8 text-blue-400" aria-hidden="true" />
            </div>
            <h2 className="text-3xl font-medium text-white mb-4">Smart AI & Gradual Shift</h2>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Take the guesswork out of frequency therapy. Our proprietary AI engine analyzes your current context to deliver the perfect acoustic environment. Plus, with our new <strong>Gradual Shift</strong> feature, you can smoothly transition between brainwave states over time, mimicking natural cognitive shifts.
            </p>
            <ul className="space-y-4">
              <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" aria-hidden="true" /><span className="text-zinc-300"><strong>Gradual Shift:</strong> Slowly transition frequencies over time for natural entrainment.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" aria-hidden="true" /><span className="text-zinc-300"><strong>Circadian UI:</strong> The app interface and recommendations adapt to your local time of day.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" aria-hidden="true" /><span className="text-zinc-300"><strong>Goal-Based Generation:</strong> Tell the app what you want to achieve, and it builds the session.</span></li>
            </ul>
          </div>

          <div className="bg-zinc-900/50 border border-white/5 rounded-3xl p-10">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-8">
              <Headphones className="w-8 h-8 text-emerald-400" aria-hidden="true" />
            </div>
            <h2 className="text-3xl font-medium text-white mb-4">Spatial Audio & Orbit Controls</h2>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Experience true immersion with our <strong>Spatial Audio</strong> engine. Enable 3D panning to have sounds orbit around you. Customize the <strong>Orbit Speed</strong> and <strong>Radius</strong> for each binaural track to create a dynamic, moving soundscape that deepens your focus or relaxation.
            </p>
          </div>

          <div className="bg-zinc-900/50 border border-white/5 rounded-3xl p-10">
            <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-8">
              <Shield className="w-8 h-8 text-indigo-400" aria-hidden="true" />
            </div>
            <h2 className="text-3xl font-medium text-white mb-4">NeuroOptimize Programs</h2>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Follow structured, multi-day programs designed by neuroscientists to achieve lasting changes in your cognitive baseline.
            </p>
            <ul className="space-y-4">
              <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" aria-hidden="true" /><span className="text-zinc-300"><strong>Progress Tracking:</strong> Monitor your listening time, streaks, and session history.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" aria-hidden="true" /><span className="text-zinc-300"><strong>Offline Listening:</strong> Download your favorite mixes and programs for use anywhere, anytime.</span></li>
              <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" aria-hidden="true" /><span className="text-zinc-300"><strong>Corporate Wellness:</strong> Deploy Neuros Box across your team with our B2B HR dashboards.</span></li>
            </ul>
            
            <a href="#pricing" onClick={(e) => { e.preventDefault(); setPage('pricing'); }} className="mt-10 px-8 py-4 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/50 text-indigo-400 rounded-full font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 flex items-center justify-center gap-2">
              Customize Your Stack <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="bg-zinc-900/50 border border-white/5 rounded-3xl p-10 md:col-span-3 lg:col-span-3 mt-4">
            <div className="w-16 h-16 rounded-2xl bg-rose-500/10 flex items-center justify-center mb-8">
              <Shield className="w-8 h-8 text-rose-400" aria-hidden="true" />
            </div>
            <h2 className="text-3xl font-medium text-white mb-4">Accessibility & Sensory Safety First</h2>
            <p className="text-zinc-400 mb-6 leading-relaxed max-w-3xl">
              We believe cognitive tools must be deeply inclusive. Neuros Box includes a suite of rigorously designed ADA and sensory processing support structures.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="border border-white/5 p-4 rounded-xl bg-black/20">
                <h4 className="text-rose-400 font-medium mb-1">Autism & SPD Sensors</h4>
                <p className="text-sm text-zinc-400">A rigorous 3.5kHz Low-Pass acoustic cut combined with a low-stimulation flat UI to ease extreme sensory burdens.</p>
              </div>
              <div className="border border-white/5 p-4 rounded-xl bg-black/20">
                <h4 className="text-rose-400 font-medium mb-1">Vestibular & Anti-Startle</h4>
                <p className="text-sm text-zinc-400">Complete UI motion limits and 3-second gentle progressive volume fades to prevent inner-ear vertigo or audiogenic shocks.</p>
              </div>
              <div className="border border-white/5 p-4 rounded-xl bg-black/20">
                <h4 className="text-rose-400 font-medium mb-1">Seizure & Tinnitus Protect</h4>
                <p className="text-sm text-zinc-400">Forces "Seizure Safe HUD" to disable strobelights alongside a master -30dB 6.5kHz notch filter masking high ringing tones.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function SleepContent({ setPage, onExit }: { setPage: (page: any) => void, onExit: () => void }) {
  return (
    <div className="animate-in fade-in duration-700">
      <SEO 
        title="Sleep Sounds & Frequencies | Neuros Box"
        description="Struggling to rest? Discover the best frequency for sleep. Our app provides powerful binaural beats for sleep, guiding your mind into deep delta waves sleep."
        url="https://neurosbox.com/sleep"
      />
      <section className="pt-32 pb-20 px-6 lg:pt-48 lg:pb-32 relative">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/10 mb-8">
            <Moon className="w-10 h-10 text-emerald-400" aria-hidden="true" />
          </div>
          <h1 className="text-5xl md:text-7xl font-light tracking-tighter text-white mb-8">
            Audio tools for <span className="font-serif italic text-emerald-400">Restful Sleep</span>
          </h1>
          <p className="text-xl text-zinc-400 leading-relaxed mb-12">
            Struggling to rest? Discover the <strong>best frequency for sleep</strong>. Our app provides powerful <strong>binaural beats for sleep</strong>, designed to help guide your mind into deep <strong>delta waves sleep</strong> so you can wake up refreshed.
          </p>
        </div>

        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8 mt-16">
          <div className="bg-zinc-900/50 border border-white/5 rounded-3xl p-8 text-center hover:bg-zinc-900/80 transition-colors">
            <Volume2 className="w-10 h-10 text-zinc-400 mx-auto mb-6" aria-hidden="true" />
            <h3 className="text-xl font-medium text-white mb-3">Sleep Sound Generator</h3>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Block out distractions with our continuous <strong>white noise for sleep</strong>, deep <strong>brown noise sleep</strong> rumbles, and balanced <strong>pink noise sleep</strong> frequencies. Customize your perfect auditory environment.
            </p>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 rounded-3xl p-8 text-center hover:bg-zinc-900/80 transition-colors">
            <Waves className="w-10 h-10 text-blue-400 mx-auto mb-6" aria-hidden="true" />
            <h3 className="text-xl font-medium text-white mb-3">Nature & Ambient</h3>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Drift off to <strong>relaxing ambient sounds</strong>. Use our <strong>sleep sound mixer</strong> to blend <strong>rain sounds for sleeping</strong> with gentle <strong>ocean sounds sleep</strong> tracks, creating <strong>calming sounds</strong> tailored to you.
            </p>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 rounded-3xl p-8 text-center hover:bg-zinc-900/80 transition-colors">
            <Brain className="w-10 h-10 text-emerald-400 mx-auto mb-6" aria-hidden="true" />
            <h3 className="text-xl font-medium text-white mb-3">Sleep Frequencies</h3>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Need a <strong>sound to fall asleep fast</strong>? Our <strong>deep sleep music</strong> uses <strong>relaxing frequencies</strong> and <strong>sleep frequencies</strong> to entrain your brain into restorative states. Discover the <strong>sounds that help you sleep</strong>.
            </p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto mt-20">
          <div className="bg-emerald-900/10 border border-emerald-500/20 rounded-3xl p-10 md:p-16 text-center">
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">Commit to the 14-Day Sleep Protocol</h2>
            <p className="text-lg text-zinc-400 mb-8 max-w-2xl mx-auto">
              Rebuilding your circadian rhythm takes time and structure. Follow our day-by-day frequency system to help guide your mind toward a more reliable, restful state.
            </p>
            <a href="#pricing" onClick={(e) => { e.preventDefault(); setPage('pricing'); }} className="px-8 py-4 bg-emerald-500 text-black rounded-full font-medium text-lg hover:bg-emerald-400 transition-all hover:scale-105 active:scale-95 inline-flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black">
              Begin Your Sleep Repair Protocol <ArrowRight className="w-5 h-5" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function FocusContent({ setPage, onExit }: { setPage: (page: any) => void, onExit: () => void }) {
  return (
    <div className="animate-in fade-in duration-700">
      <SEO 
        title="Focus Protocol & Concentration | Neuros Box"
        description="Eliminate distractions and achieve flow state with structured focus protocols. Our system is designed to complement your cognitive routine."
        url="https://neurosbox.com/focus"
      />
      <section className="pt-32 pb-20 px-6 lg:pt-48 lg:pb-32 relative">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-amber-500/10 mb-8">
            <Target className="w-10 h-10 text-amber-400" aria-hidden="true" />
          </div>
          <h1 className="text-5xl md:text-7xl font-light tracking-tighter text-white mb-8">
            The 21-Day <span className="font-serif italic text-amber-400">Deep Work System</span>
          </h1>
          <p className="text-xl text-zinc-400 leading-relaxed mb-12">
            Train your brain to enter flow states on command. Our progressive <strong>focus protocol</strong> structures your week with escalating <strong>binaural beats</strong> and Gamma frequencies, designed to complement your cognitive routine session by session.
          </p>
        </div>

        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 mt-16">
          <div className="flex flex-col justify-center">
            <h2 className="text-3xl font-medium text-white mb-6">Brainwave Meditation & Focus</h2>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Achieve peak mental performance on demand. By utilizing <strong>focus frequencies</strong> like Beta and Gamma waves, you can enhance your productivity and clarity. For deep relaxation, switch to <strong>sound meditation</strong> using Alpha and Theta waves.
            </p>
            <div className="mt-10">
              <a href="#pricing" onClick={(e) => { e.preventDefault(); setPage('pricing'); }} className="px-8 py-4 bg-amber-500 text-black rounded-full font-medium text-lg hover:bg-amber-400 transition-all hover:scale-105 active:scale-95 inline-flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black">
                Design Your Focus Routine <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </a>
            </div>
          </div>
          <div className="bg-zinc-900/50 border border-white/5 rounded-3xl p-10 relative overflow-hidden group">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?q=80&w=1000&auto=format&fit=crop')] bg-cover bg-center opacity-10 mix-blend-luminosity group-hover:opacity-20 transition-opacity duration-700"></div>
            <div className="relative z-10 flex flex-col items-center justify-center h-full text-center">
              <Music className="w-16 h-16 text-amber-400 mb-6" aria-hidden="true" />
              <h3 className="text-2xl font-medium text-white mb-4">The Ultimate Sound for Meditation</h3>
              <p className="text-zinc-400">
                Experience the clarity of <strong>binaural beats for anxiety</strong>. Our app provides the exact <strong>sound for meditation</strong> you need to center your mind, focus your energy, and achieve a state of mindfulness.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function PricingContent({ setPage, onExit }: { setPage: (page: any) => void, onExit: () => void }) {
  return (
    <div className="animate-in fade-in duration-700">
      <SEO 
        title="Pricing | Neuros Box Premium"
        description="Choose the plan that fits your cognitive goals. Upgrade to Premium to access advanced blending, circadian UI, and personalized AI insights."
        url="https://neurosbox.com/pricing"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [{
            "@type": "Question",
            "name": "Is there a free version of Neuros Box?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, we offer a free tier that includes basic sound libraries, standard frequencies, and a 20-minute session limit."
            }
          }, {
            "@type": "Question",
            "name": "What is included in Premium?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Premium includes unlimited session time, advanced Ambient Pro mixer, Solfeggio & Gamma frequencies, Circadian UI, AI Recommendation Engine, and offline listening."
            }
          }]
        }}
      />
      <section className="pt-32 pb-20 px-6 lg:pt-48 lg:pb-32 relative overflow-hidden">
        <FramerMotionVectorWaves className="absolute inset-0 w-full h-full opacity-40 pointer-events-none" speedMultiplier={0.7} />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/10 mb-8">
            <Crown className="w-10 h-10 text-emerald-400" aria-hidden="true" />
          </div>
          <h1 className="text-5xl md:text-7xl font-light tracking-tighter text-white mb-8">
            Unlock the <span className="font-serif italic text-emerald-400">Complete System</span>
          </h1>
          <p className="text-xl text-zinc-400 leading-relaxed mb-12">
            Get full access to the 14-Day Sleep Repair Protocol, the 21-Day Deep Work System, and the Advanced Pro Mixer to track your progress and restructure your habits.
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8 mt-16 relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
          {/* Free Tier */}
          <div className="bg-zinc-900/50 border border-white/5 rounded-3xl p-8 flex flex-col hover:-translate-y-2 hover:scale-[1.01] hover:border-white/15 transition-all duration-300 backdrop-blur-sm">
            <div className="mb-8">
              <h3 className="text-2xl font-medium text-white mb-2">Free</h3>
              <div className="text-4xl font-light text-white mb-4">$0<span className="text-lg text-zinc-500">/mo</span></div>
              <p className="text-zinc-400 text-sm">Perfect for getting started with basic frequency therapy.</p>
            </div>
            <ul className="space-y-4 mb-8 flex-1">
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">Basic sound library</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">Standard frequencies (Alpha, Theta, Delta)</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">20-minute session limit</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">Daily check-ins</span></li>
              <li className="flex items-start gap-3 opacity-40"><div className="w-5 h-5 flex items-center justify-center shrink-0"><div className="w-1.5 h-1.5 rounded-full bg-zinc-500"></div></div><span className="text-zinc-400 text-sm">Circadian UI</span></li>
              <li className="flex items-start gap-3 opacity-40"><div className="w-5 h-5 flex items-center justify-center shrink-0"><div className="w-1.5 h-1.5 rounded-full bg-zinc-500"></div></div><span className="text-zinc-400 text-sm">AI Personalization (Smart Mode)</span></li>
            </ul>
            <button onClick={onExit} className="w-full py-4 rounded-full border border-white/10 text-white font-medium hover:bg-white/5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black">
              Start Free Basic Plan
            </button>
          </div>

          {/* Premium Monthly */}
          <div className="bg-zinc-900/50 border border-white/5 rounded-3xl p-8 flex flex-col hover:-translate-y-2 hover:scale-[1.01] hover:border-indigo-500/40 transition-all duration-300 backdrop-blur-sm">
            <div className="mb-8">
              <h3 className="text-2xl font-medium text-white mb-2 flex items-center gap-2"><Zap className="w-5 h-5 text-indigo-400" aria-hidden="true" /> Premium Monthly</h3>
              <div className="text-4xl font-light text-white mb-4">$12.99<span className="text-lg text-zinc-500">/mo</span></div>
              <p className="text-zinc-400 text-sm">Flexible access to all cognitive and sensory tools.</p>
            </div>
            <ul className="space-y-4 mb-8 flex-1">
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">All Waves (Alpha, Beta, Theta, Delta, Gamma)</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">Full Isochronic & Solfeggio Library</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">Sensory Smoothing (Autism/ADHD)</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">Seizure Safe Modes & ADA Optimization</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">AI Recommendation Engine</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">Offline Listening</span></li>
            </ul>
            <button onClick={onExit} className="w-full py-4 rounded-full border border-indigo-500/50 text-indigo-400 font-medium hover:bg-indigo-500/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black">
              Start Monthly Pro Subscription
            </button>
          </div>

          {/* Premium Annual */}
          <div className="bg-zinc-900 border border-emerald-500/50 rounded-3xl p-8 flex flex-col relative transform md:-translate-y-4 shadow-2xl shadow-emerald-900/20 hover:-translate-y-6 hover:scale-[1.02] hover:border-emerald-500/80 transition-all duration-300 backdrop-blur-sm">
            <div className="absolute top-0 right-0 bg-emerald-500 text-black text-xs font-bold px-4 py-1.5 rounded-bl-xl rounded-tr-3xl uppercase tracking-wider">
              Best Value
            </div>
            <div className="mb-8">
              <h3 className="text-2xl font-medium text-white mb-2 flex items-center gap-2"><Sparkles className="w-5 h-5 text-emerald-400" aria-hidden="true" /> Premium Annual</h3>
              <div className="text-4xl font-light text-white mb-2">$120.00<span className="text-lg text-zinc-500">/yr</span></div>
              <p className="text-emerald-400 font-medium text-sm mb-4">Just $10.00/month</p>
              <p className="text-zinc-400 text-sm">Comprehensive cognitive, sensory, and science-backed support.</p>
            </div>
            <ul className="space-y-4 mb-8 flex-1">
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">Everything in Monthly Membership</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">Scientific Review Board Insights</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">Full Accessibility & Sensory Protocols</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">Spatial Audio & Orbit Controls</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">Gradual Shift (Natural Entrainment)</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">Progress Tracking & Analytics</span></li>
              <li className="flex items-start gap-3"><Check className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" /><span className="text-zinc-300 text-sm">Enterprise-Grade ADA Compliance</span></li>
            </ul>
            <button onClick={onExit} className="w-full py-4 rounded-full bg-emerald-500 text-black font-medium hover:bg-emerald-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black">
              Unlock the Complete System
            </button>
          </div>
        </div>
        
        <div className="max-w-3xl mx-auto mt-16 text-center">
          <div className="p-6 rounded-2xl bg-zinc-900/30 border border-white/5 inline-block">
            <h4 className="text-white font-medium mb-2">Looking for a team solution?</h4>
            <p className="text-zinc-400 text-sm mb-4">We offer Corporate Wellness plans with HR dashboards and aggregated metrics.</p>
            <button onClick={() => window.location.href = '/corporate'} className="text-emerald-400 text-sm font-medium hover:text-emerald-300 transition-colors flex items-center gap-1 mx-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-md px-2 py-1">
              View Corporate Plans <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

// --- MAIN COMPONENT ---

export default function SalesPage({ onExit }: { onExit: () => void }) {
  const [currentPage, setCurrentPage] = useState<'home' | 'features' | 'sleep' | 'focus' | 'pricing'>('home');
  const [showSampleModal, setShowSampleModal] = useState(false);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (['home', 'features', 'sleep', 'focus', 'pricing'].includes(hash)) {
        setCurrentPage(hash as any);
      } else if (!hash) {
        setCurrentPage('home');
      }
    };
    
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange(); // Check initial hash
    
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleNav = (page: 'home' | 'features' | 'sleep' | 'focus' | 'pricing') => {
    setCurrentPage(page);
    window.history.pushState(null, '', page === 'home' ? '#' : `#${page}`);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 font-sans selection:bg-emerald-500/30 overflow-x-hidden">
      {/* Header & Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/80 backdrop-blur-md border-b border-white/5 relative overflow-hidden">
        <WavelengthBackground opacity={0.35} />
        <nav className="relative z-10 flex items-center justify-between px-6 py-4">
          <a 
            href="#"
            className="flex items-center gap-2 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-md px-2 py-1"
            onClick={(e) => { e.preventDefault(); handleNav('home'); }}
          >
            <Logo className="w-6 h-6 text-emerald-500" aria-hidden="true" />
            <span className="font-medium tracking-tight text-white">Neuros Box</span>
          </a>
          <div className="flex items-center gap-6">
            <a 
              href="#features"
              onClick={(e) => { e.preventDefault(); handleNav('features'); }}
              className={`hidden md:block text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-md px-2 py-1 ${currentPage === 'features' ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
            >
              Features
            </a>
            <a 
              href="#sleep"
              onClick={(e) => { e.preventDefault(); handleNav('sleep'); }}
              className={`hidden md:block text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-md px-2 py-1 ${currentPage === 'sleep' ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
            >
              Sleep
            </a>
            <a 
              href="#focus"
              onClick={(e) => { e.preventDefault(); handleNav('focus'); }}
              className={`hidden md:block text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-md px-2 py-1 ${currentPage === 'focus' ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
            >
              Focus
            </a>
            <a 
              href="#pricing"
              onClick={(e) => { e.preventDefault(); handleNav('pricing'); }}
              className={`hidden md:block text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-md px-2 py-1 ${currentPage === 'pricing' ? 'text-white' : 'text-zinc-400 hover:text-white'}`}
            >
              Pricing
            </a>
            <button onClick={onExit} className="px-4 py-2 text-sm font-medium text-black bg-white rounded-full hover:bg-zinc-200 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black">
              Launch App
            </button>
          </div>
        </nav>
      </header>

      {/* Dynamic Content Rendering */}
      <main className="min-h-[80vh]">
        {currentPage === 'home' && <HomeContent setPage={handleNav} onExit={onExit} onPlaySample={() => setShowSampleModal(true)} />}
        {currentPage === 'features' && <FeaturesContent setPage={handleNav} onExit={onExit} />}
        {currentPage === 'sleep' && <SleepContent setPage={handleNav} onExit={onExit} />}
        {currentPage === 'focus' && <FocusContent setPage={handleNav} onExit={onExit} />}
        {currentPage === 'pricing' && <PricingContent setPage={handleNav} onExit={onExit} />}
      </main>

      {/* Global Risk Reversal & CTA for Sub-pages */}
      {currentPage !== 'home' && currentPage !== 'pricing' && (
        <section className="py-24 px-6 bg-zinc-900/30 border-t border-white/5 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-emerald-900/5 mix-blend-screen pointer-events-none"></div>
          <div className="max-w-5xl mx-auto relative z-10">
            <h2 className="text-4xl font-light text-white mb-6">Experience the cognitive shift, <span className="font-medium text-emerald-400">risk-free.</span></h2>
            
            <div className="grid md:grid-cols-3 gap-8 my-16 text-left">
              <div className="p-8 rounded-3xl bg-black/40 border border-white/5 relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-6">
                   <Target className="w-6 h-6 text-emerald-400" />
                </div>
                <h4 className="text-lg text-white font-medium mb-4">The Timeline</h4>
                <ul className="space-y-3 text-sm text-zinc-400">
                  <li className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500/50 mt-1.5 shrink-0"></div><span><strong>5 Mins:</strong> Noticeable physical relaxation</span></li>
                  <li className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500/50 mt-1.5 shrink-0"></div><span><strong>Day 7:</strong> Improved sleep metrics</span></li>
                  <li className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500/50 mt-1.5 shrink-0"></div><span><strong>Day 30:</strong> Sustained focus endurance</span></li>
                </ul>
              </div>
              
              <div className="p-8 rounded-3xl bg-black/40 border border-white/5 relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mb-6">
                   <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                </div>
                <h4 className="text-lg text-white font-medium mb-4">Who It's For</h4>
                <p className="text-zinc-400 text-sm leading-relaxed">Designed for knowledge workers, deep-focus students, and individuals struggling with racing thoughts before sleep who want an evidence-based tool, not a gimmick.</p>
              </div>

              <div className="p-8 rounded-3xl bg-black/40 border border-white/5 relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-rose-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-12 h-12 rounded-2xl bg-rose-500/10 flex items-center justify-center mb-6">
                   <Shield className="w-6 h-6 text-rose-400" />
                </div>
                <h4 className="text-lg text-white font-medium mb-4">The 30-Day Guarantee</h4>
                <p className="text-zinc-400 text-sm leading-relaxed">If your sleep latency hasn't decreased or your focused work sessions haven't improved, click one button in your settings for a full automated refund. No questions asked.</p>
              </div>
            </div>

            <a href="#pricing" onClick={(e) => { e.preventDefault(); handleNav('pricing'); }} className="px-10 py-5 bg-emerald-500 text-black rounded-full font-medium text-xl hover:bg-emerald-400 transition-all hover:scale-105 active:scale-95 inline-flex items-center justify-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black shadow-[0_0_40px_rgba(16,185,129,0.3)]">
              Unlock The Complete System <ArrowRight className="w-5 h-5" aria-hidden="true" />
            </a>
            <p className="mt-6 text-zinc-500 text-sm">Cancel anytime directly from your dashboard.</p>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="pt-20 pb-12 px-6 border-t border-white/5 bg-[#050505]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between gap-12 mb-16">
          <div className="max-w-xs">
            <a href="#" className="flex items-center gap-2 mb-4 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-md py-1" onClick={(e) => { e.preventDefault(); handleNav('home'); }}>
              <Logo className="w-5 h-5 text-emerald-500" aria-hidden="true" />
              <span className="font-medium tracking-tight text-white">Neuros Box</span>
            </a>
            <p className="text-zinc-500 text-sm leading-relaxed">Clinical-grade auditory stimulation and tracking for deep work, relaxation, and cognitive enhancement.</p>
          </div>

          <div className="flex gap-16">
            <div>
              <h4 className="text-white font-medium mb-4">Guides & Science</h4>
              <ul className="space-y-3 text-sm flex flex-col">
                <li><a href="/about" className="text-zinc-500 hover:text-emerald-400 transition-colors">About Neuros Box</a></li>
                <li><a href="/do-binaural-beats-work" className="text-zinc-500 hover:text-emerald-400 transition-colors">Do Binaural Beats Work?</a></li>
                <li><a href="/best-frequencies-focus" className="text-zinc-500 hover:text-emerald-400 transition-colors">Best Frequencies for Focus</a></li>
                <li><a href="/science" className="text-zinc-500 hover:text-emerald-400 transition-colors">Scientific Review Board</a></li>
                <li><a href="/science" className="text-zinc-500 hover:text-emerald-400 transition-colors">Clinical Research</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-medium mb-4">Legal</h4>
              <ul className="space-y-3 text-sm flex flex-col">
                <li><a href="/terms" className="text-zinc-500 hover:text-zinc-300 transition-colors">Terms of Service</a></li>
                <li><a href="/privacy" className="text-zinc-500 hover:text-zinc-300 transition-colors">Privacy Policy</a></li>
                <li><a href="/corporate" className="text-zinc-500 hover:text-zinc-300 transition-colors">Corporate Wellness</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4 text-center text-zinc-400 text-xs text-sm">
          <p>© {new Date().getFullYear()} Neuros Box. All rights reserved. • 100% Ad-Free Experience</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400 transition-colors">Sitemap</a>
            <a href="/llms.txt" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400 transition-colors">LLMs.txt</a>
            <a href="/llms-full.txt" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400 transition-colors">LLMs-Full.txt</a>
            <a href="/.well-known/ai-plugin.json" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400 transition-colors">AI Plugin</a>
            <a href="/robots.txt" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400 transition-colors">Robots.txt</a>
          </div>
        </div>
      </footer>

      {showSampleModal && <SamplePlayerModal onClose={() => setShowSampleModal(false)} />}
    </div>
  );
}
